import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../contexts/GameContext";
import { useCoop } from "../contexts/CoopContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CharacterPanel } from "../components/game/CharacterPanel";
import { TopBar } from "../components/game/TopBar";
import { HealthBar } from "../components/game/StatsDisplay";
import { getNextNodes, findNode } from "../utils/mapGenerator";
import { themeForFloor } from "../utils/themes";
import { loadChromaKeySprite } from "../utils/spriteLoader";
import { SFX, playMusic, trackForFloor } from "../utils/audio";
import { User, Backpack, Home } from "lucide-react";
import { drawBiomeDecor } from "../utils/biomeDecor";
import { drawTileDecor } from "../utils/tileDecor";
import { drawBiomeTile } from "../utils/biomeFloor";
import { InventoryModal } from "../components/game/InventoryModal";
import { runLevelFromXp } from "../utils/gameLogic";
import {
  ROOM_W, ROOM_H, TILE_W, TILE_H, PLAYER_SPEED, DOOR_RADIUS, INTERACT_RADIUS,
  NODE_LABEL, isoProject, DOOR_POS, drawWalls, drawDoor,
  drawDecoration, drawInteractable, drawPlayer, drawRemotePlayer,
} from "../utils/isoRender";
import Combat from "./Combat";
import Sanctuary from "./Sanctuary";
import ChestRoom from "./ChestRoom";
import Shop from "./Shop";
import TutorialOverlay from "../components/game/TutorialOverlay";

// === Room layout constants are imported from utils/isoRender ===

const NODE_SPRITE = {
  combat: "/sprites/enemy_small.png",
  elite: "/sprites/enemy_elite.png",
  boss: "/sprites/enemy_boss.png",
  chest: "/sprites/chest.png",
  shop: "/sprites/shop_npc.png",
};

// Quand on franchit une porte, on cache la destination : le joueur découvre la salle en entrant.
const HIDE_DOOR_DESTINATION = true;

// Distribute up to 3 doors on walls. We use N first, then W/E.
const pickDoorSides = (count) => {
  if (count <= 1) return ["N"];
  if (count === 2) return ["W", "E"];
  return ["W", "N", "E"];
};

const IsometricMap = () => {
  const { character, map, currentNodeId, floor, currentEncounter, selectNode, handleDeath, coopMode, meta } = useGame();
  const { sendPlayerPos, remotePlayersRef, lobby } = useCoop();
  const { user } = useAuth();
  const { unlockAchievement } = useProfile();
  const [panelOpen, setPanelOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const keysRef = useRef({});
  // Cartographe : révèle le type des salles derrière les portes (passif de sanctuaire)
  const revealDoorsRef = useRef(false);
  // Tutoriel solo : s'affiche automatiquement à la toute première aventure
  const [showTutorial, setShowTutorial] = useState(false);
  // Player position is in ROOM-LOCAL tile coords
  const playerPosRef = useRef({ wx: ROOM_W / 2, wy: ROOM_H - 1.5, facing: "down", moving: false, prevWx: ROOM_W / 2, prevWy: ROOM_H - 1.5 });
  const trailRef = useRef([]); // particules trailing derrière le joueur
  const lastTickRef = useRef(0);
  const spritesRef = useRef({}); // { player, enemy_small, enemy_elite, enemy_boss, chest, shop_npc }
  const animFrameRef = useRef(null);
  const interactCooldownRef = useRef(0);
  // ===== CO-OP real-time players layer =====
  const remoteSmoothRef = useRef({}); // interpolated positions { uid -> {wx, wy, facing, moving, classeId, name} }
  const posSendCooldownRef = useRef(0); // throttle my position broadcast
  const coopRef = useRef({});          // latest coop data, read inside the render loop closure
  // Keep coopRef fresh every render so the (decoupled) render loop reads up-to-date values
  coopRef.current = {
    coopMode: !!coopMode,
    myId: user?.user_id,
    myClasseId: character?.classeId,
    members: lobby?.members || [],
    sendPlayerPos,
    remotePlayersRef,
  };

  // Determine which room the player is physically in:
  // - If currentNodeId is set: that room (the last completed/visited node)
  // - Else: the starting sanctuary (map[0][0])
  const currentRoomNode = useMemo(() => {
    if (!map || !map.length) return null;
    if (currentNodeId) return findNode(map, currentNodeId);
    return map[0][0]; // start sanctuary
  }, [map, currentNodeId]);

  // Is the room "cleared"? A sanctuary/chest/etc. node is cleared once visited;
  // before that, the interactable sits at the room center.
  // We consider "cleared" = currentNodeId === thisRoomNode (player has completed it).
  const roomCleared = currentNodeId === currentRoomNode?.id;

  // Doors out of the room → only available if the room is cleared
  const nextNodes = useMemo(() => {
    if (!currentRoomNode || !roomCleared) return [];
    return getNextNodes(map, currentRoomNode.id)
      .map((id) => findNode(map, id))
      .filter(Boolean);
  }, [map, currentRoomNode, roomCleared]);

  const doors = useMemo(() => {
    const sides = pickDoorSides(nextNodes.length);
    return nextNodes.map((n, i) => ({
      node: n,
      pos: DOOR_POS[sides[i]],
    }));
  }, [nextNodes]);

  // Redirect away if no character
  useEffect(() => {
    if (coopMode) { navigate("/coop-map"); return; }
    if (!character) navigate("/");
    // Pendant un combat (overlay actif), c'est Combat.jsx qui gère la mort/le revive (Plume de Phénix).
    else if (character.stats.pv <= 0 && !currentEncounter) navigate("/gameover");
  }, [character, navigate, coopMode, currentEncounter]);

  // Cartographe : met à jour le flag de révélation des portes selon les passifs actifs
  useEffect(() => {
    revealDoorsRef.current = (character?.passifs || []).some((p) => p.special === "reveal_doors");
  }, [character]);

  // Tutoriel : auto-affichage à la première aventure solo
  useEffect(() => {
    if (!coopMode && !localStorage.getItem("checkpoint_tutorial_done")) {
      setShowTutorial(true);
    }
  }, [coopMode]);

  const closeTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem("checkpoint_tutorial_done", "1");
  };

  // Reset player position when room changes
  useEffect(() => {
    if (!currentRoomNode) return;
    // Spawn the player coming in from the SOUTH wall (entering the room)
    playerPosRef.current = {
      wx: ROOM_W / 2,
      wy: ROOM_H - 1.5,
      facing: "up",
      moving: false,
    };
    interactCooldownRef.current = performance.now() + 400;
  }, [currentRoomNode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sprites
  useEffect(() => {
    if (!character) return;
    let cancelled = false;
    const wanted = [
      ["player", `/sprites/${character.classeId}.png`],
      ["player_back", `/sprites/back/${character.classeId}.png`],
      ["player_side", `/sprites/side/${character.classeId}.png`],
      ["enemy_small", "/sprites/enemy_small.png"],
      ["enemy_elite", "/sprites/enemy_elite.png"],
      ["enemy_boss", "/sprites/enemy_boss.png"],
      ["chest", "/sprites/chest.png"],
      ["shop_npc", "/sprites/shop_npc.png"],
    ];
    (async () => {
      for (const [key, src] of wanted) {
        try {
          const c = await loadChromaKeySprite(src);
          if (cancelled) return;
          spritesRef.current[key] = c;
        } catch (e) {
          console.warn("Sprite load failed", src, e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [character?.classeId]);

  useEffect(() => { playMusic(trackForFloor(floor || 1)); }, [floor]);

  // Load sprites for ALL co-op members' classes so we can render allies on the shared map
  useEffect(() => {
    if (!coopMode) return;
    const classes = (lobby?.members || []).map((m) => m.classe_id).filter(Boolean);
    let cancelled = false;
    (async () => {
      for (const cid of classes) {
        const variants = [
          [`player_${cid}`, `/sprites/${cid}.png`],
          [`player_${cid}_back`, `/sprites/back/${cid}.png`],
          [`player_${cid}_side`, `/sprites/side/${cid}.png`],
        ];
        for (const [key, src] of variants) {
          if (spritesRef.current[key]) continue;
          try {
            const c = await loadChromaKeySprite(src);
            if (cancelled) return;
            spritesRef.current[key] = c;
          } catch (e) {
            console.warn("Ally sprite load failed", src, e);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [coopMode, lobby?.members]);

  // Keyboard
  useEffect(() => {
    const blockedKeys = new Set(["z", "q", "s", "d", "w", "a", "arrowup", "arrowdown", "arrowleft", "arrowright"]);
    const down = (e) => {
      const k = e.key.toLowerCase();
      keysRef.current[k] = true;
      if (blockedKeys.has(k)) e.preventDefault();
    };
    const up = (e) => { keysRef.current[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const triggerNode = useCallback((nodeId, viaDoor = false) => {
    if (interactCooldownRef.current > performance.now()) return;
    interactCooldownRef.current = performance.now() + 600;
    if (viaDoor) SFX.door(); else SFX.click();
    selectNode(nodeId);
  }, [selectNode]);

  // Step SFX cadence
  const stepCooldownRef = useRef(0);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;

    const resize = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const tick = (now) => {
      const dt = Math.min(0.05, (now - lastTickRef.current) / 1000 || 0);
      lastTickRef.current = now;

      // Input → movement
      if (!currentEncounter && currentRoomNode) {
        const keys = keysRef.current;
        let dx = 0, dy = 0;
        if (keys["z"] || keys["w"] || keys["arrowup"]) dy -= 1;
        if (keys["s"] || keys["arrowdown"]) dy += 1;
        if (keys["q"] || keys["a"] || keys["arrowleft"]) dx -= 1;
        if (keys["d"] || keys["arrowright"]) dx += 1;
        const len = Math.hypot(dx, dy);
        const p = playerPosRef.current;
        if (len > 0) {
          dx /= len; dy /= len;
          p.prevWx = p.wx; p.prevWy = p.wy;
          p.wx += dx * PLAYER_SPEED * dt;
          p.wy += dy * PLAYER_SPEED * dt;
          p.moving = true;
          if (Math.abs(dx) > Math.abs(dy)) p.facing = dx > 0 ? "right" : "left";
          else p.facing = dy > 0 ? "down" : "up";

          // Step SFX every ~280ms while moving
          if (now > stepCooldownRef.current) {
            SFX.step();
            stepCooldownRef.current = now + 280;
          }

          // Spawn class-specific trail particle
          if (Math.random() < 0.6) {
            trailRef.current.push({
              wx: p.wx - dx * 0.3,
              wy: p.wy - dy * 0.3,
              vx: -dx * 0.4 + (Math.random() - 0.5) * 0.3,
              vy: -dy * 0.4 + (Math.random() - 0.5) * 0.3,
              life: 0,
              maxLife: 0.6 + Math.random() * 0.3,
              size: 3 + Math.random() * 2,
              seed: Math.random(),
            });
          }

          // Clamp inside room (with margin from walls)
          p.wx = Math.max(0.6, Math.min(ROOM_W - 0.6, p.wx));
          p.wy = Math.max(0.6, Math.min(ROOM_H - 0.6, p.wy));
        } else {
          p.moving = false;
        }

        // Update trail particles
        const tr = trailRef.current;
        for (let i = tr.length - 1; i >= 0; i--) {
          const tp = tr[i];
          tp.life += dt;
          tp.wx += tp.vx * dt;
          tp.wy += tp.vy * dt;
          if (tp.life >= tp.maxLife) tr.splice(i, 1);
        }

        // Interact with the room interactable (if not yet cleared)
        if (!roomCleared && currentRoomNode) {
          const center = { wx: ROOM_W / 2, wy: ROOM_H / 2 };
          const d = Math.hypot(center.wx - p.wx, center.wy - p.wy);
          if (d < INTERACT_RADIUS) triggerNode(currentRoomNode.id, false);
        }

        // Walk into a door
        if (roomCleared) {
          for (const door of doors) {
            const d = Math.hypot(door.pos.wx - p.wx, door.pos.wy - p.wy);
            if (d < DOOR_RADIUS) { triggerNode(door.node.id, true); break; }
          }
        }

        // === CO-OP: broadcast my position to allies (throttled ~12Hz) ===
        const coTx = coopRef.current;
        if (coTx.coopMode && coTx.sendPlayerPos && now > posSendCooldownRef.current) {
          posSendCooldownRef.current = now + 80;
          coTx.sendPlayerPos({
            wx: p.wx, wy: p.wy, facing: p.facing, moving: p.moving,
            nodeId: currentRoomNode.id || null,
            classeId: coTx.myClasseId || null,
          });
        }
      }

      // === DRAW ===
      const W = canvas.clientWidth, H = canvas.clientHeight;
      ctx.clearRect(0, 0, W, H);

      const theme = themeForFloor(floor);
      // Background: dark void around the room
      const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
      bgGrad.addColorStop(0, "#020617");
      bgGrad.addColorStop(1, "#0f172a");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, W, H);

      // Draw floor tiles (only inside room bounds) — biome-themed
      const roomType = currentRoomNode?.type || "combat";

      for (let ry = 0; ry < ROOM_H; ry++) {
        for (let rx = 0; rx < ROOM_W; rx++) {
          const s = isoProject(rx + 0.5, ry + 0.5, W, H);
          const isDark = (rx + ry) % 2 === 0;
          drawBiomeTile(ctx, s.x, s.y, TILE_W, TILE_H, theme.id, rx, ry, isDark);
        }
      }

      // Draw walls (4 sides) — couleurs du biome
      drawWalls(ctx, W, H, theme.id);

      // Tile-level decoration props (mushrooms, crystals, ice shards…)
      // Drawn AFTER walls but BEFORE entities so player/interactable appear on top.
      drawTileDecor(ctx, ROOM_W, ROOM_H, W, H, now, theme.id, currentRoomNode?.id);

      // Biome decoration (atmospheric overlay)
      drawBiomeDecor(ctx, W, H, now, theme.id);

      // Doors (cut into walls)
      for (const door of doors) {
        drawDoor(ctx, door.pos, door.node, W, H, now, revealDoorsRef.current || !HIDE_DOOR_DESTINATION);
      }

      // Decoration based on room type
      drawDecoration(ctx, roomType, W, H, now);

      // Build z-order list
      const entities = [];
      if (currentRoomNode && !roomCleared) {
        entities.push({ kind: "interactable", node: currentRoomNode, depth: ROOM_W / 2 + ROOM_H / 2 });
      }
      const p = playerPosRef.current;
      entities.push({ kind: "player", depth: p.wx + p.wy });

      // === CO-OP: interpolate & queue ally sprites in the SAME room ===
      const coDr = coopRef.current;
      if (coDr.coopMode && coDr.remotePlayersRef) {
        const targets = coDr.remotePlayersRef.current || {};
        const smooth = remoteSmoothRef.current;
        const myRoom = currentRoomNode?.id || null;
        const seen = new Set();
        for (const uid of Object.keys(targets)) {
          if (uid === coDr.myId) continue;
          const tg = targets[uid];
          if (!tg) continue;
          if (Date.now() - (tg.lastUpdate || 0) > 5000) continue; // stale → drop
          if (tg.nodeId !== myRoom) continue;                     // only same room
          seen.add(uid);
          if (!smooth[uid]) smooth[uid] = { wx: tg.wx, wy: tg.wy };
          smooth[uid].wx += (tg.wx - smooth[uid].wx) * Math.min(1, dt * 12);
          smooth[uid].wy += (tg.wy - smooth[uid].wy) * Math.min(1, dt * 12);
          smooth[uid].facing = tg.facing;
          smooth[uid].moving = tg.moving;
          smooth[uid].classeId = tg.classeId;
          const m = coDr.members.find((mm) => mm.user_id === uid);
          smooth[uid].name = m?.name || "Allié";
          entities.push({ kind: "remote", pos: smooth[uid], depth: smooth[uid].wx + smooth[uid].wy });
        }
        // cleanup interpolation entries for players no longer visible
        for (const uid of Object.keys(smooth)) if (!seen.has(uid)) delete smooth[uid];
      }

      entities.sort((a, b) => a.depth - b.depth);

      for (const e of entities) {
        if (e.kind === "interactable") {
          drawInteractable(ctx, e.node, spritesRef.current, W, H, now);
        } else if (e.kind === "player") {
          drawPlayer(ctx, playerPosRef.current, { front: spritesRef.current.player, back: spritesRef.current.player_back, side: spritesRef.current.player_side }, W, H, now, character?.classeId, trailRef.current);
        } else if (e.kind === "remote") {
          const cid = e.pos.classeId;
          drawRemotePlayer(ctx, e.pos, { front: spritesRef.current[`player_${cid}`], back: spritesRef.current[`player_${cid}_back`], side: spritesRef.current[`player_${cid}_side`] }, W, H, now, cid, e.pos.name);
        }
      }

      // HUD overlay text
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(W / 2 - 100, 10, 200, 30);
      ctx.fillStyle = "#fef3c7";
      ctx.font = "bold 14px monospace";
      ctx.textAlign = "center";
      ctx.fillText(`Salle : ${NODE_LABEL[roomType] || roomType}`, W / 2, 30);

      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [currentRoomNode, doors, roomCleared, floor, currentEncounter, triggerNode]);

  if (!character) return null;
  const theme = themeForFloor(floor);

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden relative bg-slate-950" data-testid="iso-map">
      <TopBar>
        <button data-testid="open-character-panel" onClick={() => setPanelOpen(true)}
          className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-1.5 cartoon-shadow flex items-center gap-2 font-display font-bold">
          <User className="w-4 h-4" /> Personnage
        </button>
        <button data-testid="open-inventory" onClick={() => setInvOpen(true)}
          className="bg-amber-600 text-white border-4 border-slate-950 rounded-xl px-3 py-1.5 cartoon-shadow flex items-center gap-2 font-display font-bold">
          <Backpack className="w-4 h-4" /> Inventaire
          {(character.inventory?.length || 0) > 0 && (
            <span className="ml-1 bg-yellow-300 text-slate-950 rounded-full px-1.5 text-[10px] font-bold">{character.inventory.length}</span>
          )}
        </button>
        <button data-testid="pause-menu-btn"
          onClick={() => navigate("/")}
          title="Mettre la run en pause et revenir au menu (reprise via Continuer)"
          className="bg-sky-600 text-white border-4 border-slate-950 rounded-xl px-3 py-1.5 cartoon-shadow flex items-center gap-2 font-display font-bold">
          <Home className="w-4 h-4" /> Menu
        </button>
        <button data-testid="abandon-run"
          onClick={() => { if (window.confirm("Abandonner la run actuelle ?")) { if (meta?.runStartedAt && (Date.now() - meta.runStartedAt) < 10000) unlockAchievement?.("rage_quit"); handleDeath(); navigate("/"); } }}
          className="bg-rose-500 text-white border-4 border-slate-950 rounded-xl px-3 py-1.5 cartoon-shadow text-sm font-bold">
          Abandonner
        </button>
      </TopBar>

      <div className="px-4 py-2 bg-slate-800/50 border-b-2 border-slate-700 flex items-center gap-3">
        <HealthBar current={character.stats.pv} max={character.stats.pvMax} testId="player-health-bar" className="flex-1" />
        <RunLevelBar character={character} />
        <span data-testid="floor-theme-name" className="text-xs font-display font-bold uppercase tracking-wider text-slate-300 hidden sm:inline-flex items-center gap-1 bg-slate-900/70 border-2 border-slate-950 rounded-md px-2 py-1">
          <span>{theme.accent}</span> {theme.name}
        </span>
      </div>

      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        <canvas ref={canvasRef} data-testid="iso-canvas" />
        {!currentEncounter && (
          <div className="absolute bottom-3 left-3 bg-slate-900/80 text-slate-100 border-2 border-slate-700 rounded-lg px-3 py-2 text-xs font-mono pointer-events-none">
            <p className="font-bold text-yellow-300 mb-1">Déplacement</p>
            <p>ZQSD / WASD / Flèches</p>
            <p className="text-slate-400 mt-1">
              {roomCleared ? "Marche vers une porte" : "Marche au centre pour entrer"}
            </p>
          </div>
        )}
      </div>

      <CharacterPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
      <InventoryModal open={invOpen} onClose={() => setInvOpen(false)} />

      {currentEncounter?.type === "combat" && <div className="absolute inset-0 z-30"><Combat /></div>}
      {currentEncounter?.type === "sanctuary" && <div className="absolute inset-0 z-30"><Sanctuary /></div>}
      {currentEncounter?.type === "chest" && <div className="absolute inset-0 z-30"><ChestRoom /></div>}
      {currentEncounter?.type === "shop" && <div className="absolute inset-0 z-30"><Shop /></div>}
      {showTutorial && <TutorialOverlay onClose={closeTutorial} />}
    </div>
  );
};

// === Run level bar (shown next to HP bar) ===
function RunLevelBar({ character }) {
  const xp = character.runXp || 0;
  const level = character.runLevel || 1;
  const info = runLevelFromXp(xp);
  const inLevel = xp - info.currentLevelStart;
  const span = Math.max(1, info.nextLevelAt - info.currentLevelStart);
  const pct = Math.min(100, (inLevel / span) * 100);
  return (
    <div data-testid="run-level-bar" className="hidden sm:flex flex-col w-44">
      <div className="flex items-center justify-between text-[10px] mb-0.5 font-bold">
        <span className="text-yellow-300">Niv. {level}</span>
        <span className="text-slate-400">{inLevel}/{span} XP</span>
      </div>
      <div className="h-2 bg-slate-900 border border-slate-700 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-yellow-400 via-orange-400 to-rose-400" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


export default IsometricMap;
